import React, { useCallback, useState } from "react";
import { useParams } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import CircularProgress from "@mui/material/CircularProgress";

import useMutation from "../../../hooks/useMutation";
import { ADD_INVENTORY_ITEM_MUTATION } from "../../../constants/graphql/mutations";
import useQuery from "../../../hooks/useQuery";
import useNotification from "../../../hooks/notification";
import QuantitySelector from "./QuantitySelector";

export default function AddToInventoryRequest({ asset, defaultValues, onSuccess, processNewItem }) {
  const query = useQuery();
  const { projectId } = useParams();
  const { notifySuccess } = useNotification();
  const notAvailable = asset.availableQuantity <= 0;
  const [quantity, setQuantity] = useState(notAvailable ? 0 : 1);
  const [error, setError] = useState(null);
  const inventoryRequestId =
    query.get("inventoryRequestId") || query.get("orderPicking") || query.get("id");
  const [addInventoryItem, { loading }] = useMutation(ADD_INVENTORY_ITEM_MUTATION);

  const updateQuantity = useCallback(
    value => {
      if (value >= 0 && value <= asset.availableQuantity) {
        setQuantity(value);
      }
    },
    [asset.availableQuantity, setQuantity]
  );

  const handleMinus = useCallback(() => updateQuantity(quantity - 1), [quantity, updateQuantity]);

  const handlePlus = useCallback(() => updateQuantity(quantity + 1), [quantity, updateQuantity]);

  const handleUpdate = useCallback(
    event => updateQuantity(event.target.value ? parseInt(event.target.value, 10) : 0),
    [quantity, updateQuantity]
  );

  const handleAddInventoryItem = () => {
    if (quantity === 0) return;

    let variables = {
      quantity,
      projectId,
      assetId: asset.id,
      inventoryRequestId,
      ...defaultValues,
    };

    if (processNewItem) {
      variables = processNewItem(variables);
    }

    return addInventoryItem({
      variables,
      onSuccess: ({ inventoryItem }) => {
        setError(null);
        setQuantity(asset.availableQuantity > 0 ? 1 : 0);
        onSuccess?.(inventoryItem);
        notifySuccess("Asset successfully added");
      },
      onFailure: errors => {
        setError(errors.quantity || errors.asset || errors.submit);
      },
    });
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between" width="100%" gap={1}>
        <QuantitySelector
          quantity={quantity}
          onMinus={handleMinus}
          onPlus={handlePlus}
          onChange={handleUpdate}
          disabled={notAvailable || loading}
        />
        <Button
          disabled={notAvailable || loading}
          size="small"
          variant="outlined"
          onClick={handleAddInventoryItem}
          sx={{
            minWidth: 100,
            borderRadius: "25px",
            height: "36px",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            {loading && <CircularProgress size={16} />}
            Add
          </Box>
        </Button>
      </Box>
      {error && (
        <Box mt={1}>
          <FormHelperText htmlFor="barcode" error={!!error}>
            {error}
          </FormHelperText>
        </Box>
      )}
    </>
  );
}
